import FormText from 'components/form/FormText';
import { useFieldArray, useFormContext } from 'react-hook-form';
import type { DiagnosisEditFormStateType } from 'types/page/diagnosisEditFormStateType';

import type { JSX } from 'react';

/**
 * 診断作成編集 設問設定コンポーネント
 */
export default function WsDiagnosisEditQuestionOptions(props: {
  questionIndex: number;
  readOnly: boolean;
}): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 型定義 */
  /* ----------------------------------------------------------------------- */
  const {
    control,
    register,
    getValues,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<DiagnosisEditFormStateType>();

  const { append, move, remove, fields } = useFieldArray({
    control,
    name: `questions.${props.questionIndex}.options`,
  });

  // 登録可能最大件数
  const limit = 4;

  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * オプションの順番を入れ替えます。
   */
  const onMove = (optionIndex: number, upper: boolean) => {
    // 先頭の場合は上に移動不可
    if (upper && optionIndex === 0) {
      return;
    }
    // 最終行の場合は下に移動不可
    if (!upper && optionIndex === fields.length - 1) {
      return;
    }
    // 要素の入れ替え
    move(optionIndex, upper ? optionIndex - 1 : optionIndex + 1);
  };

  /**
   * オプションを削除します。
   */
  const onRemove = (optionIndex: number) => {
    // ２件以下の場合は削除不可
    if (fields.length <= 2) {
      return;
    }
    // 要素の削除
    remove(optionIndex);
  };

  /**
   * オプションを追加します。
   */
  const onAppend = () => {
    const values = getValues();
    const options = values.questions[props.questionIndex].options;

    // 上限以上の場合は追加不可
    if (options.length >= limit) {
      return;
    }
    // 要素の追加
    append({});
  };

  /**
   * 診断結果のトグルを切り替えます。
   */
  const toggleOption = (optionIndex: number, resultId: number) => {
    // 診断結果リストを取得
    const diagnosisResultList =
      getValues(
        `questions.${props.questionIndex}.options.${optionIndex}.diagnosisResultList`
      )?.split(',') || [];
    // 該当のリザルトが含まれるか確認
    const included = diagnosisResultList.includes(String(resultId));
    // 含まれていれば削除、含まれていなければ追加
    if (included) {
      diagnosisResultList.splice(diagnosisResultList.indexOf(String(resultId)), 1);
    } else {
      diagnosisResultList.push(String(resultId));
    }
    setValue(
      `questions.${props.questionIndex}.options.${optionIndex}.diagnosisResultList`,
      diagnosisResultList.join(',')
    );
  };

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      <div className="form-group form-group-md">
        <div className="row">
          <div className="col">
            <label htmlFor="option1" className="form-label">
              選択肢
            </label>
          </div>
        </div>
        {watch(`questions.${props.questionIndex}.options`).map((option, optionIndex) => {
          const diagnosisResultList = watch(
            `questions.${props.questionIndex}.options.${optionIndex}.diagnosisResultList`
          )?.split(',');
          return (
            <div key={`ws-quiz-edit-question[${props.questionIndex}]-options-${optionIndex}`}>
              <div className="form-group row align-items-center mb-2">
                <div className="col-lg">
                  <div className="form-group-row">
                    <div className="col col-number">
                      <label htmlFor="option1" className="number">
                        {optionIndex + 1}
                      </label>
                    </div>
                    <div className="col">
                      <FormText
                        name={`questions.${props.questionIndex}.options.${optionIndex}.title`}
                        placeholder="選択肢を入力しよう"
                        register={register}
                        errors={errors}
                        readOnly={props.readOnly}
                        maxLength={100}
                        className="form-control-md"
                      />
                    </div>
                  </div>
                </div>
                <div className="col-lg col-btn d-flex flex-grow-0 ms-auto text-nowrap">
                  <div className="btn-group">
                    <button
                      className="btn"
                      onClick={() => onMove(optionIndex, true)}
                      disabled={props.readOnly || optionIndex === 0}
                    >
                      <img src="/img/icon/arrow-up-black.svg" alt="" />
                    </button>
                    <button
                      className="btn"
                      onClick={() => onMove(optionIndex, false)}
                      disabled={props.readOnly || optionIndex === fields.length - 1}
                    >
                      <img src="/img/icon/arrow-up-black.svg" alt="" className="scaley-1" />
                    </button>
                    <button
                      onClick={() => onRemove(optionIndex)}
                      disabled={props.readOnly || fields.length <= 2}
                      className="btn"
                    >
                      <img src="/img/icon/trash.svg" alt="" className="filter-black" />
                    </button>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <label htmlFor="option1" className="form-label">
                    紐づけるタイプ
                  </label>
                </div>
              </div>
              <ul className="breadcrumb ms-5">
                {watch(`results`).map(result => {
                  const isSelected = diagnosisResultList?.includes(String(result.id));
                  return (
                    result.title && (
                      <li
                        className={`btn rounded-5 m-1 diagnosis-results ${
                          isSelected ? 'diagnosis-results-selected' : ''
                        }`}
                        style={{ fontSize: '0.625rem' }}
                        onClick={() => {
                          toggleOption(optionIndex, result.id);
                        }}
                        key={`ws-diagnosis-edit-question-options-${optionIndex}-result-${result.id}`}
                      >
                        {result.title}
                      </li>
                    )
                  );
                })}
              </ul>
            </div>
          );
        })}
        <div className="btn-container max-width-238 mx-auto">
          <button
            className="btn btn-common moderate-blue w-100"
            onClick={onAppend}
            disabled={fields.length >= limit || props.readOnly}
          >
            + 選択肢を追加
          </button>
        </div>
      </div>
    </>
  );
}
